import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { InfoboxComponent } from '@core/components-infobox-legacy';
import { EmailProvidersErrors, EmailProvidersUrlService } from '@core/email-providers-data';
import { EmailProviders } from '@core/email-providers-types';
import type { EmailProvider } from '@core/email-templates-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { IntegrationLegalDialogService } from '@core/integrations-legacy';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  BoxComponent,
  ButtonComponent,
  DialogBodyComponent,
  DialogHeaderComponent,
  DialogRef,
  DividerComponent,
  GroupComponent,
  HeadingComponent,
  IconComponent,
  MessageComponent,
} from '@core/user-interface-legacy';

interface EmailProviderMicrosoftDialogData {
  emailProvider: EmailProvider;
  errorMessage: string;
  returnTo?: 'academy';
  errorProvider: string;
}

@Component({
  selector: 'rt-email-provider-microsoft',
  templateUrl: './email-provider-microsoft-dialog.component.html',
  styleUrls: ['./email-provider-microsoft-dialog.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    MessageComponent,
    DividerComponent,
    GroupComponent,
    ButtonComponent,
    IconComponent,
    HeadingComponent,
    BoxComponent,
    InfoboxComponent,
    TranslatePipe,
  ],
})
export class EmailProviderMicrosoftDialogComponent
  implements DialogComponentI<EmailProviderMicrosoftDialogData, void>
{
  private readonly emailProvidersUrlService = inject(EmailProvidersUrlService);
  private readonly dialog = inject(DialogRef);
  private readonly integrationLegalDialogService = inject(IntegrationLegalDialogService);
  private readonly i18n = inject(I18nService);

  public connectUrl: string;
  public errorMessage: string | null;
  public EmailProviders = EmailProviders;
  public EmailProvidersErrors = EmailProvidersErrors;
  public emailProvider: EmailProvider;
  public errorProvider: string;

  private returnTo: EmailProviderMicrosoftDialogData['returnTo'];

  public setDialogData(data: EmailProviderMicrosoftDialogData): void {
    if (data) {
      if (data.emailProvider) {
        this.emailProvider = data.emailProvider;
      }
      if (data.errorMessage) {
        this.errorMessage = data.errorMessage;
      }
      if (data.returnTo) {
        this.returnTo = data.returnTo;
      }
      if (data.errorProvider) {
        this.errorProvider = data.errorProvider;
      }
    }
  }

  public ngOnInit(): void {
    this.connectUrl = this.emailProvidersUrlService.microsoftConnectUrl(
      this.returnTo,
      this.errorMessage === EmailProvidersErrors.onPremise,
    );
  }

  public connectClick(): void {
    this.integrationLegalDialogService
      .openLegalDialog(null, this.i18n.translate('app.email_provider_microsoft.name'), undefined, {
        helpArticleUrl: this.i18n.translate('app.email_provider_microsoft.help_article_link'),
      })
      .subscribe(result => {
        if (result && result.accepted) {
          window.location.href = this.connectUrl;
        }
      });
  }

  public cancelClick(): void {
    this.dialog.close();
  }

  public onPremiseSelected(): void {
    this.dialog.close({ openOtherProvider: true });
  }

  public get microsoftConnected(): boolean {
    return this.emailProvider.connected && this.emailProvider.provider === EmailProviders.Microsoft;
  }
}
